import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Tv, Clock, CheckCircle, Smartphone, Zap, Shield, Gift, ArrowRight, Loader2, Monitor, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// App download URLs
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.ant.tv&hl=en_IN";
const AMAZON_STORE_URL = "https://www.amazon.in/Assistive-Netspeed-Technologies-Private-Limited/dp/B0D9215FMS";

const Demo = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [activationDetails, setActivationDetails] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(true);

  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const response = await axios.get(`${API}/demo/bundles`);
        setBundles(response.data.bundles || []);
      } catch (error) {
        console.error('Error fetching bundles:', error);
      } finally {
        setLoadingBundles(false);
      }
    };
    fetchBundles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanNumber = mobileNumber.replace(/\D/g, '');
    if (cleanNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/demo/activate`, { mobile_no: cleanNumber });
      if (response.data.success) {
        setIsActivated(true);
        setActivationDetails(response.data);
        toast.success('Demo activated successfully! Download ANT TV app to start watching.');
      } else {
        toast.error(response.data.message || 'Failed to activate demo.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to activate demo.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: Tv, title: '800+ Channels' },
    { icon: Clock, title: '24 Hour Trial' },
    { icon: Shield, title: 'No Commitment' },
    { icon: Zap, title: 'Instant Access' }
  ];

  const steps = [
    { step: '1', title: 'Download ANT TV', description: 'Get from Play Store or Amazon' },
    { step: '2', title: 'Enter Demo Number', description: 'Use your registered number' },
    { step: '3', title: 'Enter OTP', description: 'Verify with OTP' },
    { step: '4', title: 'Start Watching', description: 'Enjoy Live TV' }
  ];

  return (
    <div data-testid="demo-page">
      <section className="bg-gradient-to-br from-[#0A0A0A] to-gray-900 text-white py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D92323]/20 text-[#D92323] px-4 py-2 rounded-full mb-6">
                <Gift size={18} />
                <span className="font-semibold text-sm">FREE 24-Hour Trial</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
                Get a Free Demo<span className="text-[#D92323] block">Instantly</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8">
                Experience 800+ crystal clear TV channels for free. Just enter your mobile number and get one-day access instantly.
              </p>
              <div className="flex flex-wrap gap-6 text-sm text-gray-400">
                {features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <f.icon size={16} className="text-[#D92323]" /><span>{f.title}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-2xl" data-testid="demo-form-container">
              {isActivated ? (
                <div className="text-center" data-testid="demo-success">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={40} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Demo Activated!</h3>
                  <p className="text-gray-600 mb-6">Download ANT TV app and login to start watching!</p>
                  {activationDetails && (
                    <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                      <h4 className="font-semibold text-gray-900 mb-2">Activation Details:</h4>
                      <p className="text-sm text-gray-600">Mobile: <strong>{activationDetails.mobile_no}</strong></p>
                      <p className="text-sm text-gray-600">Valid Until: <strong>{activationDetails.end_date}</strong></p>
                    </div>
                  )}
                  <div className="bg-red-50 rounded-xl p-4 mb-6 text-left border border-red-100">
                    <h4 className="font-semibold text-[#D92323] mb-3 flex items-center gap-2"><Play size={18} /> How to Watch</h4>
                    <ol className="text-sm text-gray-700 space-y-2">
                      <li className="flex items-start gap-2"><span className="bg-[#D92323] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">1</span><span>Download <strong>ANT TV</strong> app</span></li>
                      <li className="flex items-start gap-2"><span className="bg-[#D92323] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">2</span><span>Enter your demo number: <strong>{activationDetails?.mobile_no}</strong></span></li>
                      <li className="flex items-start gap-2"><span className="bg-[#D92323] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">3</span><span>Enter the OTP sent to your phone</span></li>
                      <li className="flex items-start gap-2"><span className="bg-[#D92323] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">4</span><span>Start watching Live TV!</span></li>
                    </ol>
                  </div>
                  
                  {/* Download Buttons */}
                  <div className="space-y-3">
                    <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="download-playstore-btn">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
                      Google Play Store
                    </a>
                    <a href={AMAZON_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary w-full inline-flex items-center justify-center gap-2" data-testid="download-amazon-btn">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M15.93,17.09C15.75,17.25 15.5,17.26 15.3,17.15C14.41,16.41 14.25,16.07 13.76,15.36C12.29,16.86 11.25,17.31 9.34,17.31C7.09,17.31 5.33,15.92 5.33,13.14C5.33,10.96 6.5,9.5 8.19,8.76C9.65,8.12 11.68,8 13.23,7.83V7.5C13.23,6.84 13.28,6.09 12.9,5.54C12.58,5.05 11.95,4.84 11.4,4.84C10.38,4.84 9.47,5.37 9.25,6.45C9.2,6.69 9,6.93 8.78,6.94L6.18,6.66C5.96,6.61 5.72,6.44 5.78,6.1C6.38,3.19 9,2.28 11.53,2.28C12.82,2.28 14.5,2.62 15.55,3.54C16.87,4.76 16.75,6.35 16.75,8.14V12.1C16.75,13.36 17.24,13.91 17.71,14.59C17.87,14.82 17.9,15.1 17.69,15.28L15.93,17.09M13.23,10.56V10C11.29,10 9.24,10.39 9.24,12.67C9.24,13.83 9.85,14.62 10.87,14.62C11.63,14.62 12.3,14.15 12.73,13.4C13.25,12.47 13.23,11.6 13.23,10.56M20.16,19.54C18,21.14 14.82,22 12.1,22C8.29,22 4.85,20.59 2.25,18.24C2.05,18.06 2.23,17.81 2.5,17.95C5.28,19.58 8.75,20.56 12.33,20.56C14.74,20.56 17.4,20.06 19.84,19.03C20.21,18.87 20.5,19.27 20.16,19.54M21.07,18.5C20.79,18.14 19.22,18.33 18.5,18.42C18.31,18.44 18.28,18.26 18.47,18.12C19.71,17.24 21.76,17.5 22,17.79C22.24,18.09 21.93,20.14 20.76,21.13C20.58,21.27 20.41,21.19 20.5,21C20.76,20.33 21.35,18.86 21.07,18.5Z"/></svg>
                      Amazon Appstore
                    </a>
                  </div>
                  <button onClick={() => { setIsActivated(false); setMobileNumber(''); }} className="text-[#D92323] font-semibold hover:underline mt-4 block mx-auto">Activate another number</button>
                </div>
              ) : (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Start Your Free Trial</h3>
                  <p className="text-gray-600 mb-6">Enter your mobile number to get instant access</p>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-4"><span className="text-gray-500 font-medium">+91</span></div>
                        <input type="tel" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Enter 10-digit number" className="w-full pl-14 pr-4 py-4 text-lg text-gray-900 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323] bg-white" required data-testid="demo-mobile-input" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4"><Smartphone size={20} className="text-gray-400" /></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">You'll receive OTP for verification in the app</p>
                    </div>
                    <button type="submit" disabled={isSubmitting || mobileNumber.length !== 10} className="w-full bg-[#D92323] text-white py-4 rounded-xl font-bold text-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="demo-submit-btn">
                      {isSubmitting ? (<><Loader2 size={20} className="animate-spin" /> Activating...</>) : (<><Play size={20} /> Get Free Demo</>)}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-gray-900 border-t border-gray-800">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <span className="text-gray-400 font-semibold">Supported Devices:</span>
            <div className="flex items-center gap-2 text-white"><Monitor size={24} className="text-[#D92323]" /><span className="font-medium">Android TV</span></div>
            <div className="flex items-center gap-2 text-white"><Tv size={24} className="text-[#D92323]" /><span className="font-medium">Google TV</span></div>
            <div className="flex items-center gap-2 text-white"><Play size={24} className="text-[#D92323]" /><span className="font-medium">Amazon Fire TV Stick</span></div>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge badge-default mb-4">Simple Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((item, index) => (
              <div key={index} className="relative bg-gray-50 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 bg-[#D92323] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">{item.step}</div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
                {index < 3 && <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2"><ArrowRight size={20} className="text-gray-300" /></div>}
              </div>
            ))}
          </div>
          
          {/* Download App Buttons */}
          <div className="mt-12 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Download ANT TV App</h3>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
                <div className="text-left">
                  <div className="text-xs text-gray-400">GET IT ON</div>
                  <div className="text-lg font-bold">Google Play</div>
                </div>
              </a>
              <a href={AMAZON_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#FF9900] text-black px-6 py-3 rounded-xl font-semibold hover:bg-[#e88a00] transition-colors">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor"><path d="M15.93,17.09C15.75,17.25 15.5,17.26 15.3,17.15C14.41,16.41 14.25,16.07 13.76,15.36C12.29,16.86 11.25,17.31 9.34,17.31C7.09,17.31 5.33,15.92 5.33,13.14C5.33,10.96 6.5,9.5 8.19,8.76C9.65,8.12 11.68,8 13.23,7.83V7.5C13.23,6.84 13.28,6.09 12.9,5.54C12.58,5.05 11.95,4.84 11.4,4.84C10.38,4.84 9.47,5.37 9.25,6.45C9.2,6.69 9,6.93 8.78,6.94L6.18,6.66C5.96,6.61 5.72,6.44 5.78,6.1C6.38,3.19 9,2.28 11.53,2.28C12.82,2.28 14.5,2.62 15.55,3.54C16.87,4.76 16.75,6.35 16.75,8.14V12.1C16.75,13.36 17.24,13.91 17.71,14.59C17.87,14.82 17.9,15.1 17.69,15.28L15.93,17.09M13.23,10.56V10C11.29,10 9.24,10.39 9.24,12.67C9.24,13.83 9.85,14.62 10.87,14.62C11.63,14.62 12.3,14.15 12.73,13.4C13.25,12.47 13.23,11.6 13.23,10.56M20.16,19.54C18,21.14 14.82,22 12.1,22C8.29,22 4.85,20.59 2.25,18.24C2.05,18.06 2.23,17.81 2.5,17.95C5.28,19.58 8.75,20.56 12.33,20.56C14.74,20.56 17.4,20.06 19.84,19.03C20.21,18.87 20.5,19.27 20.16,19.54M21.07,18.5C20.79,18.14 19.22,18.33 18.5,18.42C18.31,18.44 18.28,18.26 18.47,18.12C19.71,17.24 21.76,17.5 22,17.79C22.24,18.09 21.93,20.14 20.76,21.13C20.58,21.27 20.41,21.19 20.5,21C20.76,20.33 21.35,18.86 21.07,18.5Z"/></svg>
                <div className="text-left">
                  <div className="text-xs text-black/70">Available on</div>
                  <div className="text-lg font-bold">Amazon</div>
                </div>
              </a>
            </div>
            <p className="text-gray-500 text-sm mt-4">Available on Android TV, Google TV & Fire TV Stick</p>
          </div>
        </div>
      </section>

      <section className="section bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="badge badge-featured mb-4">What You Get</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Full Access for 24 Hours</h2>
              <ul className="space-y-4">
                {['800+ Live TV channels in SD, HD & 4K', 'Regional channels in 15+ languages', 'Sports, Movies, News, Kids & more', 'Works on Android TV, Google TV & Fire Stick', 'No buffering with fiber-speed streaming'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3"><CheckCircle size={20} className="text-green-500" /><span className="text-gray-700">{item}</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <h3 className="font-bold text-xl text-gray-900 mb-6">Available Channel Packages</h3>
              {loadingBundles ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div> : bundles.length > 0 ? (
                <div className="space-y-3">{bundles.slice(0,5).map((b, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div><h4 className="font-semibold text-gray-900">{b.name}</h4><p className="text-sm text-gray-500">{b.channels_count} channels</p></div>
                    <span className="text-[#D92323] font-bold">Included</span>
                  </div>
                ))}</div>
              ) : <div className="p-4 bg-gray-50 rounded-xl"><h4 className="font-semibold">All Channels Pack</h4><p className="text-sm text-gray-500">800+ channels</p></div>}
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-gradient-to-r from-red-600 to-red-700">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Experience Crystal Clear TV?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#demo-form-container" className="bg-white text-red-600 px-8 py-4 rounded-full font-bold inline-flex items-center gap-2"><Play size={18} /> Get Free Demo</a>
            <Link to="/channels" className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10">Browse Channels</Link>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12"><h2 className="text-3xl md:text-4xl font-bold text-gray-900">FAQs</h2></div>
          <div className="space-y-4">
            {[
              { q: 'Is the demo really free?', a: 'Yes! No payment required.' },
              { q: 'What devices can I use?', a: 'Android TV, Google TV, and Amazon Fire TV Stick.' },
              { q: 'How do I login?', a: 'Open ANT TV app, enter your mobile number and verify with OTP.' },
              { q: 'Where can I download the app?', a: 'ANT TV is available on Google Play Store and Amazon Appstore.' },
              { q: 'What happens after 24 hours?', a: 'Demo expires. Contact us to subscribe.' }
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6"><h3 className="font-bold text-gray-900 mb-2">{item.q}</h3><p className="text-gray-600">{item.a}</p></div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Demo;
